import React from "react";
import { Button, Popconfirm, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { ManagedDomain, ManagedRole } from "../../../types";
import type { TrashRow } from "../constants";
import { formatDate } from "../utils";

interface TrashTableProps {
  rows: TrashRow[];
  loading: boolean;
  onRestore: (type: "users" | "admins" | "domains" | "roles", id: string) => void;
  onPermanentDelete: (type: "users" | "admins" | "domains" | "roles", id: string) => void;
}

const TAG_COLORS: Record<string, string> = {
  users: "geekblue",
  admins: "purple",
  domains: "gold",
  roles: "orange",
};

const columns = (
  onRestore: TrashTableProps["onRestore"],
  onPermanentDelete: TrashTableProps["onPermanentDelete"],
): ColumnsType<TrashRow> => [
  {
    title: "Type",
    dataIndex: "type",
    key: "type",
    render: (value) => <Tag color={TAG_COLORS[value] || "default"}>{value}</Tag>,
  },
  { title: "Name", dataIndex: "name", key: "name" },
  {
    title: "Details",
    key: "details",
    render: (_, record) => {
      if (record.type === "roles") return `Level ${(record as ManagedRole).level || 5}`;
      if (record.type === "domains") return (record as ManagedDomain).url || "-";
      return record.login || "-";
    },
  },
  {
    title: "Deleted At",
    dataIndex: "deletedAt",
    key: "deletedAt",
    render: (value) => formatDate(value),
  },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    width: 250,
    render: (_, record) => (
      <Space>
        <Button onClick={() => onRestore(record.type, record.id)}>Restore</Button>
        <Popconfirm title="Delete forever?" onConfirm={() => onPermanentDelete(record.type, record.id)}>
          <Button danger>Delete Forever</Button>
        </Popconfirm>
      </Space>
    ),
  },
];

export const TrashTable: React.FC<TrashTableProps> = ({ rows, loading, onRestore, onPermanentDelete }) => (
  <Table
    rowKey="id"
    columns={columns(onRestore, onPermanentDelete)}
    dataSource={rows}
    loading={loading}
    pagination={false}
    bordered
    scroll={{ x: "max-content" }}
  />
);
